import React, { useState, useRef, useEffect } from "react";
import { BookOpen, Users, Cpu, Globe, ShieldAlert, Zap, Crosshair, Layers, Sparkles, Plus, X, Info, Trash2 } from "lucide-react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import CommonInput from "../Input/CommonInput";
import CommonTextArea from "../Input/CommonTextArea";
import { ChipInput } from "../Input/chipInput";
import SecondaryButton from "../Button/SecondaryButton";
import { PrimaryButton } from "../Button/PrimaryButton";
import TextButton from "../Button/TextButton";
import IconButton from "../Button/IconButton";
import AccordionCard from "../Accordion/AccordionCard";
import CustomCheckbox from "../Checkbox/CustomCheckbox";
import Badge from "../Badge/Badge";
import CustomModal from "../Modal/CustomModal";
import CustomCommonTooltip from "../Upload/ToolTip/CustomCommonTooltip";
import { mergeSignalConfig, extractEnabledTriggers } from "@/shared/utils/signalTriggerConfig";

const CATEGORY_ICONS = { BookOpen, Users, Cpu, Globe, ShieldAlert, Zap, Crosshair, Layers, Sparkles };
const GROUPS = [
  { type: "company", title: "Company level triggers", description: "Choose the company events you want AI to research." },
  { type: "industry", title: "Industry level triggers", description: "Choose changes across the wider industry and market." },
];

const countTriggers = (categories) => {
  const { companyLevelSalesTrigger, industryLevelTrigger } = extractEnabledTriggers(categories);
  return companyLevelSalesTrigger.length + industryLevelTrigger.length;
};

export default function SignalTriggerConfig({
  initialCategories = null,
  companyLevelTriggers = [],
  industryLevelTriggers = [],
  onChange,
  onSave,
  className = "",
  showSaveButton = false,
  isSaving = false,
}) {
  const [categories, setCategories] = useState(() => mergeSignalConfig(initialCategories, companyLevelTriggers, industryLevelTriggers));
  const [expanded, setExpanded] = useState({});
  const [showUnselected, setShowUnselected] = useState({});
  const [triggerModal, setTriggerModal] = useState({
    isOpen: false,
    categoryId: null,
    categoryName: "",
    name: "",
    description: "",
    sources: [],
    error: "",
  });
  const [categoryModal, setCategoryModal] = useState({
    isOpen: false,
    type: "company",
    name: "",
    description: "",
    error: "",
  });

  const initialSignature = JSON.stringify([initialCategories, companyLevelTriggers, industryLevelTriggers]);
  const previousInitial = useRef(initialSignature);

  useEffect(() => {
    if (previousInitial.current === initialSignature) return;
    previousInitial.current = initialSignature;
    if (JSON.stringify(initialCategories) === JSON.stringify(categories)) return;
    setCategories(mergeSignalConfig(initialCategories, companyLevelTriggers, industryLevelTriggers));
    // Categories are compared only when incoming saved data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSignature, initialCategories, companyLevelTriggers, industryLevelTriggers]);

  const commit = (updated, action) => {
    setCategories(updated);
    onChange?.(extractEnabledTriggers(updated));
    // Diagnostics: selection counts only, no user-entered content.
    if (action) console.debug("[SignalTriggerConfig]", {
      action,
      categories: updated.filter((category) => category.selected).length,
      triggers: countTriggers(updated),
    });
  };

  const updateCategory = (id, update, action) => commit(
    categories.map((category) => category.id === id ? update(category) : category),
    action,
  );

  const selectCategories = (type, ids) => {
    const selectedIds = new Set(ids);
    const finalCategories = categories.map((category) => {
      if (category.type !== type) return category;
      const selected = selectedIds.has(category.id);
      if (selected === category.selected) return category;
      return { ...category, selected, signals: category.signals.map((signal) => ({ ...signal, enabled: selected })) };
    });

    commit(finalCategories, "select categories");
    setExpanded((current) => {
      const updated = { ...current };
      finalCategories.forEach((category) => {
        if (category.type === type) {
          if (!selectedIds.has(category.id)) {
            updated[category.id] = false;
          } else if (current[category.id] === undefined) {
            updated[category.id] = true;
          }
        }
      });
      return updated;
    });
  };

  const removeCategory = (groupType, categoryId) => {
    const isCustom = categoryId.startsWith("custom_cat_");
    if (isCustom) {
      commit(categories.filter((cat) => cat.id !== categoryId), "delete custom category");
      setExpanded((current) => {
        const updated = { ...current };
        delete updated[categoryId];
        return updated;
      });
    } else {
      selectCategories(groupType, categories.filter((c) => c.type === groupType && c.selected && c.id !== categoryId).map((c) => c.id));
    }
  };

  const updateSignal = (categoryId, signalId, changes, action) => updateCategory(categoryId, (category) => ({
    ...category,
    signals: category.signals.map((signal) => signal.id === signalId ? { ...signal, ...changes } : signal),
  }), action);

  const removeSignal = (categoryId, signalId) => updateCategory(
    categoryId,
    (category) => ({
      ...category,
      signals: category.signals.filter((signal) => signal.id !== signalId),
    }),
    "remove signal trigger"
  );

  const openTriggerModal = (category) => {
    setTriggerModal({
      isOpen: true,
      categoryId: category.id,
      categoryName: category.name,
      name: "",
      description: "",
      sources: [],
      error: "",
    });
  };

  const closeTriggerModal = () => {
    setTriggerModal((current) => ({ ...current, isOpen: false, error: "" }));
  };

  const handleCreateTrigger = () => {
    const trimmedName = triggerModal.name.trim();
    if (!trimmedName) {
      setTriggerModal((current) => ({ ...current, error: "Please enter a signal trigger type" }));
      return;
    }

    const newSignal = {
      id: `sig_${crypto.randomUUID()}`,
      name: trimmedName,
      description: triggerModal.description.trim(),
      sources: Array.isArray(triggerModal.sources) ? triggerModal.sources : [],
      enabled: true,
    };

    updateCategory(
      triggerModal.categoryId,
      (category) => ({
        ...category,
        selected: true,
        signals: [newSignal, ...category.signals],
      }),
      "add trigger"
    );

    setExpanded((current) => ({ ...current, [triggerModal.categoryId]: true }));
    closeTriggerModal();
  };

  const openCategoryModal = (type, categoryName = "") => {
    setCategoryModal({
      isOpen: true,
      type,
      name: categoryName,
      description: "",
      error: "",
    });
  };

  const closeCategoryModal = () => {
    setCategoryModal((current) => ({ ...current, isOpen: false, error: "" }));
  };

  const handleCreateCategory = () => {
    const trimmedName = categoryModal.name.trim();
    if (!trimmedName) {
      setCategoryModal((current) => ({ ...current, error: "Please enter a category name" }));
      return;
    }

    const existing = categories.find(
      (cat) => cat.type === categoryModal.type && cat.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existing) {
      updateCategory(existing.id, (cat) => ({ ...cat, selected: true }), "select category");
      setExpanded((current) => ({ ...current, [existing.id]: true }));
      closeCategoryModal();
      return;
    }

    const newCatId = `custom_cat_${crypto.randomUUID()}`;
    const newCategory = {
      id: newCatId,
      type: categoryModal.type,
      name: trimmedName,
      description: categoryModal.description.trim() || "Custom trigger category",
      icon: "Zap",
      selected: true,
      signals: [],
    };

    commit([...categories, newCategory], "add custom category");
    setExpanded((current) => ({ ...current, [newCatId]: true }));
    closeCategoryModal();
  };

  const selected = categories.filter((category) => category.selected);
  const triggerCount = countTriggers(categories);

  return (
    <div className={twMerge(clsx("space-y-6 rounded-2xl border border-gray-200 bg-white p-5 text-gray-900 dark:border-gray-800 dark:bg-[#071317] dark:text-gray-100", className))}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Signal triggers</h2>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">Select a category to add all its triggers. Use checkboxes to adjust your selection and expand a trigger to edit its details.</p>
        </div>
        <Badge variant="secondary">{selected.length} categories / {triggerCount} triggers</Badge>
      </div>

      {GROUPS.map((group) => {
        const available = categories.filter((category) => category.type === group.type);
        const chosen = available.filter((category) => category.selected);
        return (
          <section key={group.type} className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold">{group.title}</h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{group.description}</p>
            </div>
            <ChipInput
              defaultChips={chosen.map((category) => category.id)}
              dropdownOptions={available.map((category) => ({
                value: category.id,
                label: category.name,
                description: `${category.signals.length} triggers. ${category.description || ""}`,
              }))}
              allowCustomChips={true}
              showOptionDescription
              showChipTooltip
              placeholder="Type to find or add custom category..."
              secondaryPlaceholder="Add another category..."
              onChange={(ids) => selectCategories(group.type, ids)}
              onCreateCustomChip={(categoryName) => openCategoryModal(group.type, categoryName)}
              className="bg-white dark:bg-gray-900"
              classNameChips="text-xs py-1"
            />
            {chosen.length === 0 && <p className="rounded-xl border border-dashed border-gray-200 p-5 text-center text-xs text-gray-500 dark:border-gray-800">No categories selected. Search or add one above.</p>}
            {chosen.map((category) => {
              const Icon = CATEGORY_ICONS[category.icon] || BookOpen;
              const signals = category.signals.filter((signal) => signal.enabled);
              const unselectedCount = category.signals.length - signals.length;
              const visibleSignals = showUnselected[category.id] ? category.signals : signals;
              return (
                <AccordionCard
                  key={category.id}
                  title={category.name}
                  subtitle={category.description}
                  icon={<Icon size={18} />}
                  badge={<Badge variant="secondary" size="xs">{category.signals.length ? `${signals.length} of ${category.signals.length} selected` : "Category is the trigger"}</Badge>}
                  expanded={Boolean(expanded[category.id])}
                  onToggle={() => setExpanded((current) => ({ ...current, [category.id]: !current[category.id] }))}
                  actions={<IconButton ariaLabel={`Remove ${category.name} category`} className="hover:text-red-500" onClick={() => removeCategory(group.type, category.id)}><X size={16} /></IconButton>}
                >
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-gray-500">{category.signals.length ? `${signals.length} selected triggers` : "Using the category name as the trigger"}</span>
                      {(unselectedCount > 0 || showUnselected[category.id]) && <TextButton onClick={() => setShowUnselected((current) => ({ ...current, [category.id]: !current[category.id] }))}>{showUnselected[category.id] ? "Show selected only" : `Show unselected (${unselectedCount})`}</TextButton>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <TextButton disabled={!unselectedCount} onClick={() => updateCategory(category.id, (item) => ({ ...item, signals: item.signals.map((signal) => ({ ...signal, enabled: true })) }), "restore all triggers")}>Select all</TextButton>
                      <TextButton disabled={!signals.length} className="text-gray-500" onClick={() => updateCategory(category.id, (item) => ({ ...item, signals: item.signals.map((signal) => ({ ...signal, enabled: false })) }), "clear selection")}>Clear selection</TextButton>
                      <SecondaryButton title="Add trigger" icon={<Plus size={14} />} onClick={() => openTriggerModal(category)} />
                    </div>
                  </div>
                  {category.signals.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-xs text-gray-500 dark:border-gray-800">
                      <p className="font-medium text-gray-700 dark:text-gray-300">No separate triggers in this category.</p>
                      <p className="mt-1 text-gray-500">The category name is used as the trigger. Click &ldquo;Add trigger&rdquo; above to add specific triggers instead.</p>
                    </div>
                  ) : !visibleSignals.length ? (
                    <p className="py-5 text-center text-xs text-gray-500">No triggers selected. Show unselected triggers to choose individually, or select all.</p>
                  ) : null}
                  <div className={visibleSignals.length ? "overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800" : ""}>
                    {visibleSignals.map((signal) => (
                      <SignalEditor
                        key={signal.id}
                        signal={signal}
                        onUpdate={(changes) => updateSignal(category.id, signal.id, changes)}
                        onToggle={() => updateSignal(category.id, signal.id, { enabled: !signal.enabled }, "toggle trigger")}
                        onRemove={() => removeSignal(category.id, signal.id)}
                      />
                    ))}
                  </div>
                </AccordionCard>
              );
            })}
          </section>
        );
      })}
      {showSaveButton && <div className="flex justify-end"><PrimaryButton startContent={false} isLoading={isSaving} onClick={() => onSave?.(extractEnabledTriggers(categories))}>Save signal configuration</PrimaryButton></div>}

      {/* Modal: Add Signal Trigger */}
      <CustomModal
        isOpen={triggerModal.isOpen}
        width="540px"
        className="!p-0"
        overlayClassName="z-[100000005]"
      >
        <div className="flex w-full flex-col p-6">
          <div className="flex w-full items-start justify-between gap-4 border-b border-gray-100 pb-4 dark:border-gray-800">
            <div className="min-w-0">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-secondary">
                {triggerModal.categoryName || "Signal Trigger"}
              </p>
              <h2 className="mt-0.5 text-base font-semibold text-gray-900 dark:text-gray-100">
                Add Signal Trigger
              </h2>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Define the trigger type, what AI should detect, and the research sources to monitor.
              </p>
            </div>
            <IconButton ariaLabel="Close dialog" onClick={closeTriggerModal} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </IconButton>
          </div>

          <div className="w-full space-y-4 py-4 text-left">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-200">
                Signal trigger type <span className="text-red-500">*</span>
              </label>
              <CommonInput
                value={triggerModal.name}
                onChange={(e) => setTriggerModal((current) => ({ ...current, name: e.target.value, error: "" }))}
                placeholder="e.g. Leadership Expansion, Series B Funding, New Patent Filing"
                autoFocus
                wrapperClassName={triggerModal.error ? "border-red-500" : ""}
              />
              {triggerModal.error && (
                <p className="mt-1 text-xs text-red-500">{triggerModal.error}</p>
              )}
            </div>

            <div>
              <label className="mb-1.5 flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                What AI should research and detect
                <CustomCommonTooltip content="Describe the event, evidence, or criteria AI should look for.">
                  <span tabIndex={0} aria-label="Description help">
                    <Info size={13} className="text-gray-400" />
                  </span>
                </CustomCommonTooltip>
              </label>
              <CommonTextArea
                value={triggerModal.description}
                onChange={(e) => setTriggerModal((current) => ({ ...current, description: e.target.value }))}
                rows={3}
                placeholder="Describe the signals, keywords, or evidence AI should detect..."
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-200">
                Research sources
              </label>
              <ChipInput
                defaultChips={triggerModal.sources}
                onChange={(sources) => setTriggerModal((current) => ({ ...current, sources }))}
                allowCustomChips={true}
                placeholder="Type a source (e.g. LinkedIn, SEC Filings) and press Enter..."
                secondaryPlaceholder="Add source..."
                className="bg-white dark:bg-gray-900"
                classNameChips="text-xs py-1"
              />
            </div>
          </div>

          <div className="mt-2 flex w-full items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
            <SecondaryButton title="Cancel" onClick={closeTriggerModal} />
            <PrimaryButton title="Add trigger" onClick={handleCreateTrigger} startContent={false}>
              Add trigger
            </PrimaryButton>
          </div>
        </div>
      </CustomModal>

      {/* Modal: Add Custom Category */}
      <CustomModal
        isOpen={categoryModal.isOpen}
        width="480px"
        className="!p-0"
        overlayClassName="z-[100000005]"
      >
        <div className="flex w-full flex-col p-6">
          <div className="flex w-full items-start justify-between gap-4 border-b border-gray-100 pb-4 dark:border-gray-800">
            <div className="min-w-0">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-secondary">
                {categoryModal.type === "company" ? "Company Level Triggers" : "Industry Level Triggers"}
              </p>
              <h2 className="mt-0.5 text-base font-semibold text-gray-900 dark:text-gray-100">
                Add Custom Category
              </h2>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Create a new category to group custom signal triggers for research.
              </p>
            </div>
            <IconButton ariaLabel="Close dialog" onClick={closeCategoryModal} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </IconButton>
          </div>

          <div className="w-full space-y-4 py-4 text-left">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-200">
                Category name <span className="text-red-500">*</span>
              </label>
              <CommonInput
                value={categoryModal.name}
                onChange={(e) => setCategoryModal((current) => ({ ...current, name: e.target.value, error: "" }))}
                placeholder="e.g. Executive Moves, Strategic Partnerships, Regulatory Filings"
                autoFocus
                wrapperClassName={categoryModal.error ? "border-red-500" : ""}
              />
              {categoryModal.error && (
                <p className="mt-1 text-xs text-red-500">{categoryModal.error}</p>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-200">
                Description (optional)
              </label>
              <CommonTextArea
                value={categoryModal.description}
                onChange={(e) => setCategoryModal((current) => ({ ...current, description: e.target.value }))}
                rows={2}
                placeholder="Brief description of the triggers in this category..."
              />
            </div>
          </div>

          <div className="mt-2 flex w-full items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
            <SecondaryButton title="Cancel" onClick={closeCategoryModal} />
            <PrimaryButton title="Create category" onClick={handleCreateCategory} startContent={false}>
              Create category
            </PrimaryButton>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}

function SignalEditor({ signal, onUpdate, onToggle, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(signal.name);
  useEffect(() => setName(signal.name), [signal.name]);
  const saveName = () => {
    const trimmed = name.trim();
    if (!trimmed) { setName(signal.name); return; }
    if (trimmed !== signal.name) onUpdate({ name: trimmed });
  };
  return (
    <AccordionCard
      title={signal.name || "Untitled trigger"}
      compact
      leadingContent={<CustomCheckbox checked={signal.enabled} onChange={onToggle} ariaLabel={`Select ${signal.name || "untitled trigger"}`} />}
      expanded={expanded}
      onToggle={() => setExpanded((current) => !current)}
      actions={
        onRemove ? (
          <IconButton ariaLabel={`Remove ${signal.name || "trigger"}`} className="text-gray-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <Trash2 size={14} />
          </IconButton>
        ) : null
      }
    >
      <div className="space-y-3">
        <div className="flex items-end gap-3">
          <label className="min-w-0 flex-1">
            <span className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-200">Signal trigger type</span>
            <CommonInput value={name} onChange={(event) => setName(event.target.value)} onBlur={saveName} onKeyDown={(event) => {
              if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); }
              if (event.key === "Escape") { event.preventDefault(); setName(signal.name); }
            }} wrapperClassName="min-w-0" placeholder="Enter signal trigger type" />
          </label>
        </div>
        <label className="block">
          <span className="mb-1.5 flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-200">
            What AI should research and detect
            <CustomCommonTooltip content="Describe the event, evidence or criteria AI should look for. Your edits are saved with this trigger."><span tabIndex={0} aria-label="Description help"><Info size={13} className="text-gray-400" /></span></CustomCommonTooltip>
          </span>
          <CommonTextArea value={signal.description} onChange={(event) => onUpdate({ description: event.target.value })} rows={3} placeholder="Describe the evidence AI should find..." className="min-w-0" />
        </label>
        <div>
          <p className="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-200">Research sources</p>
          <ChipInput defaultChips={signal.sources} onChange={(sources) => onUpdate({ sources })} placeholder="Type a source and press Enter..." secondaryPlaceholder="Add source..." className="bg-white dark:bg-gray-900" classNameChips="text-[11px] py-0.5" />
        </div>
      </div>
    </AccordionCard>
  );
}

